import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LocalHospital as ClinicIcon } from '@mui/icons-material';

import { publicService, ClinicInfo, PatientRegistrationData } from '../services/publicService';
import { getApiError } from '../utils/apiError';

const steps = ['Personal Information', 'Contact & Emergency', 'Medical Information', 'Review & Submit'];

const PatientRegistrationPage: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();

  const [activeStep, setActiveStep] = useState(0);
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<PatientRegistrationData>>({});

  const { register, handleSubmit, formState: { errors }, trigger, getValues } = useForm<PatientRegistrationData>();

  // Load clinic info
  useEffect(() => {
    const loadClinic = async () => {
      if (!clinicId) {
        setError('Invalid clinic ID');
        setLoading(false);
        return;
      }

      try {
        const clinicData = await publicService.getClinicInfo(parseInt(clinicId));
        setClinic(clinicData);
      } catch (err) {
        console.error('Error loading clinic:', err);
        setError('Clinic not found');
      } finally {
        setLoading(false);
      }
    };

    loadClinic();
  }, [clinicId]);

  const handleNext = async () => {
    // Validate current step
    const fieldsToValidate = getFieldsForStep(activeStep);
    const isValid = await trigger(fieldsToValidate as any);

    if (!isValid) {
      return;
    }

    // Save current step data
    const currentValues = getValues();
    setFormData({ ...formData, ...currentValues });

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const onSubmit = async (data: PatientRegistrationData) => {
    if (!clinicId) return;

    setSubmitting(true);
    try {
      await publicService.registerPatient(parseInt(clinicId), data);
      toast.success('Registration successful! We will contact you soon.');
      setTimeout(() => {
        // Redirect to a thank you page or reset
        setActiveStep(0);
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      console.error('Error registering patient:', err);
      toast.error(getApiError(err, 'Registration failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldsForStep = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['id_number', 'first_name', 'last_name', 'date_of_birth'];
      case 1:
        return ['email', 'phone'];
      case 2:
        return []; // Medical info is optional
      case 3:
        return [];
      default:
        return [];
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                {...register('id_number', {
                  required: 'ID number is required',
                  pattern: {
                    value: /^\d{9}$/,
                    message: 'ID number must be 9 digits'
                  }
                })}
                fullWidth
                label="ID Number (תעודת זהות)"
                error={!!errors.id_number}
                helperText={errors.id_number?.message}
                inputProps={{ maxLength: 9 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('first_name', { required: 'First name is required' })}
                fullWidth
                label="First Name"
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('last_name', { required: 'Last name is required' })}
                fullWidth
                label="Last Name"
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('date_of_birth', { required: 'Date of birth is required' })}
                fullWidth
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth?.message}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('phone', { required: 'Phone number is required' })}
                fullWidth
                label="Phone Number"
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('emergency_contact_name')}
                fullWidth
                label="Emergency Contact Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('emergency_contact_phone')}
                fullWidth
                label="Emergency Contact Phone"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                {...register('reason_for_visit')}
                fullWidth
                label="Reason for Visit"
                multiline
                rows={3}
                helperText="Optional: Tell us why you're seeking care"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('medical_history')}
                fullWidth
                label="Medical History"
                multiline
                rows={3}
                helperText="Optional: Any relevant medical conditions"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('current_medications')}
                fullWidth
                label="Current Medications"
                multiline
                rows={2}
                helperText="Optional: List any medications you're currently taking"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('insurance_provider')}
                fullWidth
                label="Insurance Provider"
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('insurance_id')}
                fullWidth
                label="Insurance ID"
                helperText="Optional"
              />
            </Grid>
          </Grid>
        );

      case 3:
        const values = { ...formData, ...getValues() };
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Please review your information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Name:</Typography>
                <Typography>{values.first_name} {values.last_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date of Birth:</Typography>
                <Typography>{values.date_of_birth}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Email:</Typography>
                <Typography>{values.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone:</Typography>
                <Typography>{values.phone}</Typography>
              </Grid>
              {values.emergency_contact_name && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Emergency Contact:</Typography>
                  <Typography>{values.emergency_contact_name}</Typography>
                </Grid>
              )}
              {values.emergency_contact_phone && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Emergency Phone:</Typography>
                  <Typography>{values.emergency_contact_phone}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !clinic) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
        <Alert severity="error">{error || 'Clinic not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 800,
          width: '100%',
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
          <ClinicIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1">
              {clinic.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Patient Registration
            </Typography>
          </Box>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ minHeight: 300 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit Registration'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default PatientRegistrationPage;
